import { BRAND_SEO_KEYWORDS } from "../config/seoKeywords";
import ApiEngine from "./ApiEngine.js";
import { ApiKey, ApiUrl } from "./Constant.js";

// Cache for SEO keywords from API
let cachedSeoKeywords = null;
let keywordsFetchPromise = null;

function normalizeKey(value) {
  try {
    return String(value || "").trim().toLowerCase();
  } catch (e) {
    return "";
  }
}

/**
 * Fetches SEO keywords from the API (cached)
 * @returns {Promise<string>} SEO keywords string
 */
async function fetchSeoKeywordsFromApi() {
  // Return cached value if available
  if (cachedSeoKeywords !== null) {
    return cachedSeoKeywords;
  }

  // If a fetch is already in progress, return that promise
  if (keywordsFetchPromise) {
    return keywordsFetchPromise;
  }

  // Start new fetch
  keywordsFetchPromise = (async () => {
    try {
      const responseJson = await ApiEngine.get(ApiUrl._API_GET_SEO_KEYWORDS);
      
      if (responseJson[ApiKey._API_SUCCESS_KEY] && responseJson[ApiKey._API_DATA_KEY]) {
        const keywords = String(responseJson[ApiKey._API_DATA_KEY]).trim();
        cachedSeoKeywords = keywords || "";
        return cachedSeoKeywords;
      }
    } catch (error) {
      console.warn("Failed to fetch SEO keywords from API:", error);
    } finally {
      keywordsFetchPromise = null;
    }
    
    // Return empty string on failure
    cachedSeoKeywords = "";
    return "";
  })();

  return keywordsFetchPromise;
}

/**
 * Returns a comma-separated keyword string for the current brand.
 * Priority:
 * 1) env override `REACT_APP_SEO_KEYWORDS`
 * 2) API fetched keywords (if available synchronously from cache)
 * 3) `BRAND_SEO_KEYWORDS[brandName]` (fallback to hardcoded)
 * 4) fallback derived from brand/title/description
 * 
 * Note: For async fetching, use getSeoKeywordsAsync instead
 */
export function getSeoKeywords({ brandName, title, description }) {
  const envKeywords = normalizeKey(process.env.REACT_APP_SEO_KEYWORDS);
  if (envKeywords) return process.env.REACT_APP_SEO_KEYWORDS;

  // Use cached API keywords if available
  if (cachedSeoKeywords !== null && cachedSeoKeywords) {
    return cachedSeoKeywords;
  }

  // Fallback to hardcoded keywords
  const brandKey = normalizeKey(brandName);
  if (brandKey && BRAND_SEO_KEYWORDS[brandKey]) return BRAND_SEO_KEYWORDS[brandKey];

  const parts = [];
  if (brandName) parts.push(String(brandName).trim());
  if (title) parts.push(String(title).trim());

  // include up to ~6 tokens from description (keeps it small + avoids giant keywords tag)
  if (description) {
    const tokens = String(description)
      .replace(/\s+/g, " ")
      .trim()
      .split(" ")
      .slice(0, 6)
      .join(" ");
    if (tokens) parts.push(tokens);
  }

  // De-dupe while preserving order
  const seen = new Set();
  const deduped = parts
    .map((p) => p.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .filter((p) => {
      const k = normalizeKey(p);
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });

  return deduped.join(", ");
}

/**
 * Async version that fetches SEO keywords from API
 * Priority:
 * 1) env override `REACT_APP_SEO_KEYWORDS`
 * 2) API fetched keywords
 * 3) `BRAND_SEO_KEYWORDS[brandName]` (fallback to hardcoded)
 * 4) fallback derived from brand/title/description
 */
export async function getSeoKeywordsAsync({ brandName, title, description }) {
  const envKeywords = normalizeKey(process.env.REACT_APP_SEO_KEYWORDS);
  if (envKeywords) return process.env.REACT_APP_SEO_KEYWORDS;

  // Try to fetch from API
  const apiKeywords = await fetchSeoKeywordsFromApi();
  if (apiKeywords) return apiKeywords;

  // Fallback to hardcoded keywords
  const brandKey = normalizeKey(brandName);
  if (brandKey && BRAND_SEO_KEYWORDS[brandKey]) return BRAND_SEO_KEYWORDS[brandKey];

  const parts = [];
  if (brandName) parts.push(String(brandName).trim());
  if (title) parts.push(String(title).trim());

  if (description) {
    const tokens = String(description)
      .replace(/\s+/g, " ")
      .trim()
      .split(" ")
      .slice(0, 6)
      .join(" ");
    if (tokens) parts.push(tokens);
  }

  const seen = new Set();
  const deduped = parts
    .map((p) => p.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .filter((p) => {
      const k = normalizeKey(p);
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });

  return deduped.join(", ");
}


